import { ref, watch, computed, type ComputedRef } from 'vue'
import { useNotificationsQuery } from './queries'
import { useMarkNotificationsReadMutation, useMarkNotificationsUnreadMutation } from './mutations'
import type { Notification } from './types'

function stripInlineStyles(html: string | undefined | null): string {
  if (!html) return ''
  return html
    .replace(/&nbsp;/g, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/<o:p><\/o:p>/g, '')
    .replace(/<([a-zA-Z][a-zA-Z0-9]*)[^>]*>\s*<\/\1>/g, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/(\d+\.?\d*)pt/g, (match, value) => {
      const ptValue = parseFloat(value)
      const pxValue = Math.round(ptValue * (4 / 3))
      return `${pxValue}px`
    })
}

export interface SimpleNotification {
  id: string
  message: string
  shortMessage: string
  longMessage: string
  date: string
  read: boolean
  subject: string
  // Controls Unread-tab filtering without affecting the unread dot
  unreadForFilter: boolean
}

export interface UseNotificationsBehavior {
  autoSelectFirst?: boolean
  markReadOnSelect?: boolean
}

export function useNotifications(
  options?: ComputedRef<{ limit?: string; skip?: string; term?: string }>,
  behavior: UseNotificationsBehavior = {},
) {
  const { autoSelectFirst = true, markReadOnSelect = true } = behavior
  const selectedNotification = ref<SimpleNotification | null>(null)
  const selectedNotificationIds = ref<Set<string>>(new Set())
  // Keep items visible in the Unread view until a refetch occurs
  const holdUnreadIds = ref<Set<string>>(new Set())

  // Use the real API query from the folder with pagination options
  const queryOptions = computed(() => options?.value || {})
  const { data: notificationsData, isLoading } = useNotificationsQuery(queryOptions)

  // Convert complex Notification objects to simple format
  const notifications = computed<SimpleNotification[]>(() => {
    if (!notificationsData.value?.Data) return []

    return notificationsData.value.Data.map(
      (notification: Notification): SimpleNotification => ({
        id: notification.Id,
        message: notification.ShortMessage?.Plain || notification.LongMessage?.Plain || '',
        date: notification.TimeStamp,
        // Show true read state (controls the dot)
        read: notification.Read,
        // Keep listed under Unread until we refetch
        unreadForFilter: !notification.Read || holdUnreadIds.value.has(notification.Id),
        shortMessage: notification.ShortMessage?.Plain || '',
        longMessage: notification.LongMessage?.Markdown
          ? stripInlineStyles(notification.LongMessage?.Html || '')
          : notification.LongMessage?.Plain,

        subject: notification.Subject,
      }),
    )
  })

  // Get total count of notifications
  const totalNotifications = computed(() => {
    return notificationsData.value?.Count || 0
  })

  // Use the real mutation from the folder
  const markAsReadMutation = useMarkNotificationsReadMutation()

  const markMultipleAsReadMutation = useMarkNotificationsReadMutation()

  const markSelectedAsUnreadMutation = useMarkNotificationsUnreadMutation()

  const markAllAsReadMutation = useMarkNotificationsReadMutation()

  // Separate mutation for selecting notifications (suppresses toast)
  const selectMarkAsReadMutation = useMarkNotificationsReadMutation()

  // Methods
  const markAsRead = (notificationId: string) => {
    // Keep it visible in the Unread tab until refetch
    holdUnreadIds.value.add(notificationId)
    markAsReadMutation.mutate([notificationId])
  }

  const markSelectedAsRead = () => {
    if (selectedNotificationIds.value.size > 0) {
      const ids = Array.from(selectedNotificationIds.value)
      ids.forEach((id) => holdUnreadIds.value.add(id))
      markMultipleAsReadMutation.mutate(ids)
    }
  }

  const markSelectedAsUnread = () => {
    if (selectedNotificationIds.value.size > 0) {
      const ids = Array.from(selectedNotificationIds.value)
      // No need to keep them held as unread if server marks them unread
      ids.forEach((id) => holdUnreadIds.value.delete(id))
      markSelectedAsUnreadMutation.mutate(ids)
    }
  }

  // Expose helpers to mark arbitrary id sets (used for mixed selections)
  const markIdsAsRead = (ids: string[]) => {
    if (ids.length > 0) {
      ids.forEach((id) => holdUnreadIds.value.add(id))
      markMultipleAsReadMutation.mutate(ids)
    }
  }

  const markIdsAsUnread = (ids: string[]) => {
    if (ids.length > 0) {
      ids.forEach((id) => holdUnreadIds.value.delete(id))
      markSelectedAsUnreadMutation.mutate(ids)
    }
  }

  const markAllAsRead = () => {
    const allIds = notifications.value.map((n) => n.id)
    if (allIds.length > 0) {
      allIds.forEach((id) => holdUnreadIds.value.add(id))
      markAllAsReadMutation.mutate(allIds)
    }
  }

  const selectNotification = (notification: SimpleNotification | null) => {
    selectedNotification.value = notification

    // Mark the notification as read when selected, but only if it's not already read
    if (notification && !notification.read && markReadOnSelect) {
      // Optimistically update the selected item
      selectedNotification.value = {
        ...notification,
        read: true,
        unreadForFilter: true, // keep it visible in Unread list
      }
      // Keep it visible in the Unread tab until refetch
      holdUnreadIds.value.add(notification.id)
      selectMarkAsReadMutation.mutate([notification.id])
    }
  }

  // Select the first notification when data is loaded
  watch(
    notifications,
    (newNotifications) => {
      if (
        autoSelectFirst &&
        newNotifications &&
        newNotifications.length > 0 &&
        !selectedNotification.value
      ) {
        selectNotification(newNotifications[0])
      }
    },
    { immediate: true },
  )

  const toggleNotificationSelection = (notificationId: string) => {
    if (selectedNotificationIds.value.has(notificationId)) {
      selectedNotificationIds.value.delete(notificationId)
    } else {
      selectedNotificationIds.value.add(notificationId)
    }
  }

  const clearSelection = () => {
    selectedNotificationIds.value.clear()
  }

  const unreadIds = computed(() => new Set(notifications.value.filter((n) => !n.read).map((n) => n.id)))

  return {
    notifications,
    totalNotifications,
    unreadIds,
    selectedNotification,
    selectedNotificationIds,
    isLoading,
    markAsRead,
    markSelectedAsRead,
    markSelectedAsUnread,
    markIdsAsRead,
    markIdsAsUnread,
    markAllAsRead,
    selectNotification,
    toggleNotificationSelection,
    clearSelection,
  }
}
